/**
 * 예약관리 API — chat-server `/api/v1/admin/reservation-resources*`.
 *
 * ── 통합설정 › 회사 관리 (2026-08-25) ─────────────────────────────────────────
 * 관리 콘솔(8444)의 같은 이름 api 모듈을 **그대로 옮겨 온 것**이다. 함수 이름·인자·반환 모양이
 * 한 글자도 다르지 않다 — 두 화면이 같은 서버 라우트를 부르므로 원본과 대조하기 쉽게 둔다.
 * 콘솔은 BFF `/api` + 이 상대 경로, 8443 은 baseURL 이 이미 `/api/v1` 이라 **같은 문자열이
 * 같은 라우트에 닿는다**(서버·BFF 변경 0). 권한은 chat-server 가 ADMIN/SYSTEM_ADMIN 으로 판정.
 */
import { apiClient } from './client';

/** 담당자 요약(주담당·부담당 공통) — 사용자 사이트 좌측 목록에도 같은 형태가 쓰인다. */
export interface ReservationManager {
  id: string;
  name: string;
  position: string | null;
  avatarUrl: string | null;
  email?: string;
}

export interface ReservationResource {
  id: string;
  /** 목록 그룹 라벨(회의실 · 빔프로젝터 · 도서대여 · 개인장비 …). */
  category: string;
  /** 예약명. */
  name: string;
  location: string | null;
  equipmentInfo: string | null;
  /** 보유 수량 — 같은 시간대에 이 수량까지 동시 예약된다(1 = 배타). */
  capacity: number;
  /** 'time' = 시각 단위, 'day' = 일 단위 대여. */
  bookingUnit: 'time' | 'day';
  slotMinutes: number;
  /** 운영 시작/종료(자정 기준 분). */
  openMinute: number;
  closeMinute: number;
  requiresApproval: boolean;
  maxDaysAhead: number;
  isActive: boolean;
  sortOrder: number;
  owner: ReservationManager | null;
  managers: ReservationManager[];
  createdAt: string | null;
  updatedAt: string | null;
}

/** 등록/수정 입력 — 수정 시에는 전부 optional(부분 수정). */
export interface ReservationResourceInput {
  category?: string;
  name?: string;
  location?: string | null;
  equipmentInfo?: string | null;
  capacity?: number;
  bookingUnit?: 'time' | 'day';
  slotMinutes?: number;
  openMinute?: number;
  closeMinute?: number;
  requiresApproval?: boolean;
  maxDaysAhead?: number;
  /** 주담당 userId. */
  ownerUserId?: string;
  /** 부담당 userId 목록 — 보낸 값으로 전체 교체된다. */
  managerUserIds?: string[];
  isActive?: boolean;
  sortOrder?: number;
}

export async function listReservationResources(): Promise<ReservationResource[]> {
  const { data } = await apiClient.get<{ resources: ReservationResource[] }>('/admin/reservation-resources');
  // 방어 가드 — 비정형 응답에서 소비처 map 크래시 차단(레포 관례).
  return Array.isArray(data?.resources) ? data.resources : [];
}

export async function createReservationResource(input: ReservationResourceInput): Promise<ReservationResource> {
  const { data } = await apiClient.post<{ resource: ReservationResource }>('/admin/reservation-resources', input);
  return data.resource;
}

export async function updateReservationResource(
  id: string,
  patch: ReservationResourceInput,
): Promise<ReservationResource> {
  const { data } = await apiClient.patch<{ resource: ReservationResource }>(`/admin/reservation-resources/${id}`, patch);
  return data.resource;
}

export async function deleteReservationResource(id: string): Promise<void> {
  await apiClient.delete(`/admin/reservation-resources/${id}`);
}
